import React, { useMemo, useRef } from 'react';


import { ContainsEditableProps, useEditor, _div, _span, _heading } from '@toasttab/sites-components';
import classnames from 'classnames';
import ColorContrastChecker from 'color-contrast-checker';

import { BackgroundImage, PaddingEnum, Review, ThemeTypeEnum } from 'src/apollo/sites';

import Carousel from 'shared/components/common/carousel/Carousel';
import { getBackgroundColorOrImageModule, getSectionPaddingModule } from 'shared/components/common/editor_context/editableUtils';
import Stars from 'shared/components/common/stars';

import { useThemeColorScheme } from 'public/components/default_template/meta/useTheme';

const WHITE = '#FFFFFF';
const BLACK = '#000000';
const STAR_YELLOW = '#FFE606';
const CONTRAST_CHECKER = new ColorContrastChecker();

type ReviewCardProps = {
  review: Review;
  index: number;
  fillColor: string;
  textColor: string;
  backgroundColor?: string | null;
  theme?: ThemeTypeEnum | null;
} & ContainsEditableProps;

const ReviewCard = (props: ReviewCardProps) => {
  const pathToContent = `${props.editPath}.content`;
  const pathToAuthorName = `${props.editPath}.authorName`;
  const pathToReviewStarRating = `${props.editPath}.rating`;
  const colorsStyle = props?.backgroundColor && props.theme === ThemeTypeEnum.Wide
    ? {
      backgroundColor: props?.backgroundColor,
      color: props.textColor
    }
    : {};
  return (
    <div className={classnames('reviewCard', { paddedSection: props.theme === ThemeTypeEnum.Wide })}
      style={colorsStyle}>
      <div className="reviewWrapper">
        {props.theme === ThemeTypeEnum.Wide && <span className="startQuote" aria-hidden="true">&ldquo;</span>}
        <blockquote className="reviewContent txt-body-md">
          <div className="quote">
            {props.theme !== ThemeTypeEnum.Wide && <span aria-hidden="true">&ldquo;</span>}
            <_span html={props.review.content} editPath={pathToContent} />
            {props.theme !== ThemeTypeEnum.Wide && <span className="endQuote" aria-hidden="true">&rdquo;</span>}
          </div>
          <div className="authorRating">
            <_div className="author txt-title-sm" html={props.review.authorName} editPath={pathToAuthorName} />
            <div className="rating txt-body-lg">
              <Stars fillColor={props.fillColor} count={props.review.rating} size={15} editPath={pathToReviewStarRating} />
            </div>
          </div>
        </blockquote>
      </div>
    </div>
  );
};


type ReviewsProps = {
  id: string
  header?: string | null;
  reviews: Review[];
  fillColor?: string;
  theme?: ThemeTypeEnum | null;
  backgroundColor?: string | null;
  backgroundImage?: BackgroundImage | null;
  padding?: PaddingEnum | null;
} & ContainsEditableProps;


const Reviews = ({ padding = PaddingEnum.Medium, ...props }: ReviewsProps) => {
  const { useEditableRef, isEditor } = useEditor();

  const carouselRef = useRef<HTMLDivElement>(null);
  const { editableRef } = useEditableRef<HTMLDivElement>({
    name: 'review section',
    actions: ['move-up', 'move-down', 'duplicate', 'delete'],
    path: props.editPath,
    skipRef: carouselRef,
    schema: {
      fields: [
        getSectionPaddingModule(props.editPath, padding),
        getBackgroundColorOrImageModule(props.editPath, props.backgroundColor, props.backgroundImage)
      ]
    }
  });

  const { reviewTextBackgroundColor, outerBackgroundColor } = useMemo(() => {
    if(props.theme === ThemeTypeEnum.Wide) {
      // if wide, then the reviews are displayed with a black background unless the background color is set.
      const backgroundColor = props.backgroundColor || BLACK;
      return { reviewTextBackgroundColor: backgroundColor, outerBackgroundColor: backgroundColor };
    } else {
      // if not wide, then the review text is displayed with a white background, the outerBackgroundColor is white if not set.
      return { reviewTextBackgroundColor: WHITE, outerBackgroundColor: props.backgroundColor || WHITE };
    }
  }, [props.theme, props.backgroundColor]);

  const getColorFromTheme = useThemeColorScheme();
  const themeTextColor = getColorFromTheme(theme => theme.colorScheme.text.default, WHITE);

  const starColor = useMemo(() => {
    return [props.fillColor, STAR_YELLOW, WHITE, BLACK].filter(color => CONTRAST_CHECKER.isLevelAA(reviewTextBackgroundColor, color, 14))[0] || WHITE;
  }, [props.fillColor, reviewTextBackgroundColor]);

  const reviewTextColor = useMemo(() => {
    return [themeTextColor, WHITE, BLACK].filter(color => CONTRAST_CHECKER.isLevelAA(reviewTextBackgroundColor, color, 14))[0] || WHITE;
  }, [reviewTextBackgroundColor, themeTextColor]);
  const outerTextColor = useMemo(() => {
    return [themeTextColor, WHITE, BLACK].filter(color => CONTRAST_CHECKER.isLevelAA(outerBackgroundColor, color, 14))[0] || WHITE;
  }, [outerBackgroundColor, themeTextColor]);
  const textStyle = { color: outerTextColor };

  if(!props.reviews?.length) {
    if(isEditor) {
      return <div ref={editableRef} className={classnames('reviewsSection', 'emptySection', props.theme)} ></div>;
    }
    return null;
  }

  const pathToReview = (idx: number) => `${props.editPath}.reviews[${idx}]`;

  return (
    <div ref={editableRef}
      className={classnames('reviewsSection', props.theme, { defaultBackground: props.backgroundColor === null } )}
      data-testid="reviews-section" style={textStyle}>
      <div className="paddedSection">
        <_heading
          styleLevel={2}
          className="sectionHeader"
          html={props.theme !== ThemeTypeEnum.Wide ? props.header || 'What people are saying...' : ''}
          editPath={`${props.editPath}.reviewHeader`} />
      </div>
      <Carousel
        id={props.id}
        theme={props.theme}
        primaryColor={props.fillColor}
        whiteInactiveArrow={props.theme === ThemeTypeEnum.Wide}
        carouselRef={carouselRef}
        textColor={outerTextColor}
        ariaLabel="Reviews"
        items={props.reviews.map((review, index) =>
          <div key={`review${index}`} className="reviewCardWrapper carouselItem">
            <ReviewCard editPath={pathToReview(index)} review={review} index={index} fillColor={starColor} textColor={reviewTextColor}
              theme={props.theme} backgroundColor={props.backgroundColor} />
          </div>)} />
    </div>
  );
};

export default Reviews;
